/*
 * Typo tolerance for theme-native search.
 *
 * Shopify's search/predictive-search APIs do no fuzzy matching: "gooky" returns
 * zero products even though "Goosky" is a brand in the catalogue. This script
 * corrects the term against the store's own vocabulary (vendors, product types,
 * trending terms - see snippets/search-fuzzy-dictionary.liquid) and is used in
 * two places:
 *   1. predictive-search.js - retries the dropdown query with the correction.
 *   2. the /search page      - when the page renders zero results, it reloads
 *                              with the corrected term (`hd_from` carries the
 *                              original so the notice can be shown and no
 *                              redirect loop is possible).
 */
window.HDSearchFuzzy = (function () {
  var HAS_DIGIT = /\d/;
  var SPLIT = /[^a-z0-9]+/;
  var MIN_WORD = 4; // shorter words are too ambiguous to "fix"
  var words = null;
  var cache = {};

  function build() {
    if (words) return;
    var raw = (window.hdSearchFuzzy && window.hdSearchFuzzy.terms) || [];
    var seen = {};
    raw.forEach(function (entry) {
      if (!entry) return;
      String(entry)
        .toLowerCase()
        .split(SPLIT)
        .forEach(function (word) {
          if (word.length >= 3 && !HAS_DIGIT.test(word)) seen[word] = true;
        });
    });
    words = Object.keys(seen);
  }

  /*
   * Damerau-Levenshtein (optimal string alignment) so a transposition such as
   * "gosoky" counts as one edit instead of two. Bails out early once the row
   * minimum exceeds max, which keeps a full dictionary scan cheap.
   */
  function distance(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;

    var prev2 = [];
    var prev = [];
    var curr = [];
    var i, j;

    for (j = 0; j <= b.length; j++) prev[j] = j;

    for (i = 1; i <= a.length; i++) {
      curr[0] = i;
      var rowMin = curr[0];
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var value = Math.min(
          curr[j - 1] + 1, // insertion
          prev[j] + 1, // deletion
          prev[j - 1] + cost // substitution
        );
        if (
          i > 1 &&
          j > 1 &&
          a.charAt(i - 1) === b.charAt(j - 2) &&
          a.charAt(i - 2) === b.charAt(j - 1)
        ) {
          value = Math.min(value, prev2[j - 2] + 1); // transposition
        }
        curr[j] = value;
        if (value < rowMin) rowMin = value;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev.slice();
      prev = curr.slice();
    }

    return prev[b.length];
  }

  function maxDistanceFor(word) {
    if (word.length <= 4) return 1;
    if (word.length <= 7) return 2;
    return 3;
  }

  // A word that prefixes a real dictionary word is not a typo ("goo" -> goosky).
  function isPrefixOfKnownWord(word) {
    for (var i = 0; i < words.length; i++) {
      if (words[i].indexOf(word) === 0) return true;
    }
    return false;
  }

  function bestMatch(word) {
    var max = maxDistanceFor(word);
    var best = null;
    var bestDistance = max + 1;

    for (var i = 0; i < words.length; i++) {
      var candidate = words[i];
      if (candidate === word) return null; // exact hit, nothing to fix
      var d = distance(word, candidate, max);
      if (d > max) continue;
      // Typos rarely land on the first character, so only trust a differing
      // first letter when it is a single edit away.
      if (candidate.charAt(0) !== word.charAt(0) && d > 1) continue;
      if (
        d < bestDistance ||
        (d === bestDistance && best && candidate.length < best.length)
      ) {
        best = candidate;
        bestDistance = d;
      }
    }

    return best;
  }

  /*
   * Correct a whole phrase word by word. Words with digits (model codes such as
   * "s2", "rs4"), short words and valid prefixes are left untouched.
   */
  function correct(phrase) {
    var term = (phrase || '').toString().trim();
    if (!term) return { changed: false, corrected: term };
    if (cache[term]) return cache[term];

    build();
    var result = { changed: false, corrected: term };

    if (words.length) {
      var changed = false;
      var parts = term.split(/\s+/).map(function (part) {
        var lower = part.toLowerCase();
        if (lower.length < MIN_WORD || HAS_DIGIT.test(lower)) return part;
        if (isPrefixOfKnownWord(lower)) return part;
        var match = bestMatch(lower);
        if (!match) return part;
        changed = true;
        return match;
      });

      if (changed) result = { changed: true, corrected: parts.join(' ') };
    }

    cache[term] = result;
    return result;
  }

  function popularTerms() {
    var list = (window.hdSearchFuzzy && window.hdSearchFuzzy.trending) || [];
    return list.map(String).filter(Boolean);
  }

  /*
   * Pick the popular search closest to what the visitor typed, so a dead end
   * still shows something related ("gooky rs" -> "goosky rs4"). Falls back to the
   * first configured popular search when nothing looks similar.
   */
  function bestPopular(phrase) {
    var list = popularTerms();
    if (!list.length) return null;

    var queryWords = (phrase || '')
      .toLowerCase()
      .split(SPLIT)
      .filter(function (word) {
        // 2-letter fragments still prefix-match ("te" -> "terranault 8x8").
        return word.length >= 2;
      });

    var best = null;
    var bestScore = 0;

    list.forEach(function (candidate) {
      var candidateWords = candidate.toLowerCase().split(SPLIT);
      var score = 0;

      queryWords.forEach(function (queryWord) {
        var max = maxDistanceFor(queryWord);
        candidateWords.forEach(function (candidateWord) {
          if (candidateWord === queryWord) score += 3;
          else if (candidateWord.indexOf(queryWord) === 0 || queryWord.indexOf(candidateWord) === 0) score += 2;
          else if (distance(queryWord, candidateWord, max) <= max) score += 1;
        });
      });

      if (score > bestScore) {
        bestScore = score;
        best = candidate;
      }
    });

    return best || list[0];
  }

  function headingFor(term) {
    var template =
      (window.search_strings && window.search_strings.products_for) || 'Products for “[terms]”';
    var node = document.createElement('h3');
    node.className = 'search-block-title quickSearchFallback__heading text-left';
    node.innerHTML = '<span class="text"></span>';
    node.querySelector('.text').textContent = template.replace('[terms]', term);
    return node;
  }

  function noticeHtml(original, corrected) {
    var template =
      (window.search_strings && window.search_strings.corrected_notice) ||
      'No results for “[original]”. Showing results for “[corrected]”.';
    var text = template
      .replace('[original]', original)
      .replace('[corrected]', corrected);
    var node = document.createElement('p');
    node.className = 'hd-search-corrected';
    node.textContent = text;
    return node;
  }

  /*
   * The single query suggestion Shopify itself ranks nearest to the term
   * ("te" -> "tengu"). Section rendering rather than /search/suggest.json on
   * purpose: the JSON endpoint answers 401 behind `shopify theme dev`.
   */
  function nearestSuggestion(term) {
    if (!term) return Promise.resolve(null);
    var url =
      ((window.routes && window.routes.predictive_search_url) || '/search/suggest') +
      '?q=' + encodeURIComponent(term) +
      '&' + encodeURIComponent('resources[type]') + '=query' +
      '&' + encodeURIComponent('resources[limit]') + '=1' +
      '&section_id=predictive-search-queries';

    return fetch(url)
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (text) {
        if (!text) return null;
        var link = new DOMParser().parseFromString(text, 'text/html').querySelector('li a');
        var value = link && (link.getAttribute('aria-label') || link.textContent);
        value = value && value.trim().replace(/\s+/g, ' ');
        return value || null;
      })
      .catch(function () { return null; });
  }

  /*
   * "da" -> "da*": Shopify's own prefix matching (options[prefix]=last) only
   * kicks in from 3 characters, but an explicit trailing * works from any
   * length AND applies to every word. Wildcarding the visitor's words keeps
   * the search on THEIR term ("da" finds the dampers directly) instead of
   * swapping it for another one. Returns null when there is nothing to add or
   * the query uses search syntax that should be left alone.
   */
  function wildcard(phrase) {
    var term = (phrase || '').trim();
    if (!term || /["':]/.test(term)) return null;

    var out = term
      .split(/\s+/)
      .map(function (word) {
        var bare = word.replace(/\*+$/, '');
        return bare ? bare + '*' : word;
      })
      .join(' ');

    return out === term ? null : out;
  }

  // True when `q` is just the wildcarded form of `original` ("da*" of "da") -
  // that swap needs no "showing results for..." notice.
  function sameButWildcard(q, original) {
    if (!q || !original) return false;
    var strip = function (value) {
      return String(value).replace(/\*/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
    };
    return strip(q) === strip(original);
  }

  // The dropdown's own results template doubles as a cheap probe: a detached
  // .products-grid with children, or null when the term finds nothing.
  function fetchGrid(term) {
    var searchUrl = (window.routes && window.routes.search_url) || '/search';
    return fetch(searchUrl + '?q=' + encodeURIComponent(term) + '&type=product&options%5Bprefix%5D=last&view=ajax_dropdown')
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (text) {
        if (!text) return null;
        var grid = new DOMParser().parseFromString(text, 'text/html').querySelector('.products-grid');
        return grid && grid.children.length ? grid : null;
      })
      .catch(function () { return null; });
  }

  /*
   * The best replacement query for a dead-end term, verified to actually
   * return products: the wildcarded term itself first (keeps the visitor's
   * words), then the query suggestion Shopify ranks nearest. Resolves to the
   * winning term or null.
   */
  function bestAlternative(typedTerm) {
    var wild = wildcard(typedTerm);
    var wildProbe = wild
      ? fetchGrid(wild).then(function (grid) { return grid ? wild : null; })
      : Promise.resolve(null);

    return wildProbe.then(function (aliveWild) {
      if (aliveWild) return aliveWild;
      return nearestSuggestion(typedTerm).then(function (suggested) {
        if (!suggested || suggested.toLowerCase() === typedTerm.toLowerCase()) return null;
        return fetchGrid(suggested).then(function (grid) { return grid ? suggested : null; });
      });
    });
  }

  /*
   * Closest-results rescue for a dead-end term, shared by the /search page and
   * the dropdown: try the query suggestion Shopify ranks nearest first, then
   * the closest configured popular search - keeping the first term whose
   * search actually returns products. Resolves to { term, grid } (grid is a
   * detached .products-grid ready to insert) or null.
   */
  function rescueGrid(typedTerm) {
    function tryTerms(terms) {
      if (!terms.length) return Promise.resolve(null);
      return fetchGrid(terms[0]).then(function (grid) {
        return grid ? { term: terms[0], grid: grid } : tryTerms(terms.slice(1));
      });
    }

    return nearestSuggestion(typedTerm).then(function (suggested) {
      var candidates = [];
      if (suggested) candidates.push(suggested);
      var popular = bestPopular(typedTerm);
      if (popular && popular !== suggested) candidates.push(popular);
      return tryTerms(candidates);
    });
  }

  return {
    correct: correct,
    noticeHtml: noticeHtml,
    popularTerms: popularTerms,
    bestPopular: bestPopular,
    wildcard: wildcard,
    sameButWildcard: sameButWildcard,
    nearestSuggestion: nearestSuggestion,
    bestAlternative: bestAlternative,
    rescueGrid: rescueGrid,
    headingFor: headingFor
  };
})();

/*
 * /search page behaviour. main-search.liquid exposes window.hdSearchNoResults
 * when a performed search returned nothing.
 */
(function () {
  var FALLBACK_LIMIT = 8;

  /*
   * Still nothing to show: load the closest results into the empty state so
   * the page is never a dead end - Shopify's nearest query suggestion first,
   * the closest popular search after that (see rescueGrid). Uses the
   * dropdown's own template (`view=ajax_dropdown`); the Section Rendering API
   * is not an option because /search?section_id=main answers 404.
   */
  function loadFallbackGrid(typedTerm) {
    var holder = document.querySelector('[data-search-fallback-results]');
    if (!holder || holder.dataset.loaded) return;
    holder.dataset.loaded = '1';

    var searchUrl = (window.routes && window.routes.search_url) || '/search';

    window.HDSearchFuzzy.rescueGrid(typedTerm)
      .then(function (rescue) {
        if (!rescue) {
          holder.dataset.loaded = '';
          return;
        }
        var grid = rescue.grid;
        while (grid.children.length > FALLBACK_LIMIT) {
          grid.removeChild(grid.lastElementChild);
        }

        var query =
          '?q=' + encodeURIComponent(rescue.term) + '&type=product&options%5Bprefix%5D=last';
        var viewAll = document.createElement('div');
        viewAll.className = 'productViewAll';
        var link = document.createElement('a');
        link.className = 'button button-view-all animate-arrow';
        link.href = searchUrl + query;
        link.textContent = rescue.term;
        viewAll.appendChild(link);

        holder.appendChild(window.HDSearchFuzzy.headingFor(rescue.term));
        holder.appendChild(grid);
        holder.appendChild(viewAll);

        if (window.sharedFunctions && typeof window.sharedFunctions.swapHoverVideoProductCard === 'function') {
          window.sharedFunctions.swapHoverVideoProductCard();
        }
      })
      .catch(function () {
        holder.dataset.loaded = '';
      });
  }

  function run() {
    var params = new URLSearchParams(window.location.search);
    var original = params.get('hd_from');

    // Arrived here from a correction: explain what happened. A plain wildcard
    // swap ("da" -> "da*") keeps the visitor's own words, so it needs no notice.
    if (original) {
      if (!window.HDSearchFuzzy.sameButWildcard(params.get('q'), original)) {
        var notice = window.HDSearchFuzzy.noticeHtml(original, params.get('q') || '');
        var anchor =
          document.querySelector('[id^="SearchSection"] .page-header') ||
          document.querySelector('#CollectionProductGrid');
        if (anchor && anchor.parentNode) {
          anchor.parentNode.insertBefore(notice, anchor.nextSibling);
        }
      }
      // Never correct twice (no redirect loops), but if the correction itself
      // found nothing, still rescue the page with popular-search products.
      loadFallbackGrid(original);
      return;
    }

    var data = window.hdSearchNoResults;
    /*
     * Cookiebot's auto-blocking re-runs inline scripts after its own init, so
     * the flag from main-search.liquid may not be set yet when this deferred
     * script executes. The zero-results markup is already parsed by then -
     * detect it and take the term from the URL instead of trusting timing.
     */
    if ((!data || !data.terms) && document.querySelector('.collection--empty [data-search-fallback-results]')) {
      data = { terms: (params.get('q') || '').trim() };
    }
    if (!data || !data.terms) return;

    function redirectTo(term) {
      params.set('q', term);
      params.set('hd_from', data.terms);
      if (!params.get('type')) params.set('type', 'product');
      if (!params.get('options[prefix]')) params.set('options[prefix]', 'last');
      window.location.replace(window.location.pathname + '?' + params.toString());
    }

    var fix = window.HDSearchFuzzy.correct(data.terms);
    if (fix.changed) {
      redirectTo(fix.corrected);
      return;
    }

    /*
     * No dictionary correction matched. Before settling for the 0-results
     * page, look for a replacement query that really returns products: the
     * wildcarded term itself first ("da" -> "da*" finds the dampers on the
     * visitor's own words), then Shopify's nearest indexed suggestion. hd_from
     * marks the swap (notice only when the words actually changed) and stops
     * this from ever happening twice. Only when nothing similar exists does
     * the page stay on 0 results with the popular-searches rescue block.
     */
    window.HDSearchFuzzy.bestAlternative(data.terms).then(function (alternative) {
      if (alternative) {
        redirectTo(alternative);
        return;
      }
      loadFallbackGrid(data.terms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
